import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Editor from '@monaco-editor/react';
import { registerReactTypings } from '../utils/monacoReactTypes';
import { getFileLanguage, isPngFile } from '../vibeCodingUtils';

const CodeEditor = ({
  editorValue,
  imagePreviewUrl,
  selectedPath,
  isFileLoading,
  onEditorChange,
  onSave,
  theme,
  readOnly,
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const onSaveRef = useRef(onSave);
  const [markerInfo, setMarkerInfo] = useState({ errors: 0, warnings: 0 });

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const updateMarkerCounts = (monaco, model) => {
    if (!monaco || !model) {
      setMarkerInfo({ errors: 0, warnings: 0 });
      return;
    }
    const markers = monaco.editor.getModelMarkers({ resource: model.uri });
    let errors = 0;
    let warnings = 0;
    markers.forEach((m) => {
      if (m.severity === monaco.MarkerSeverity.Error) errors += 1;
      else if (m.severity === monaco.MarkerSeverity.Warning) warnings += 1;
    });
    setMarkerInfo({ errors, warnings });
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    registerReactTypings(monaco);

    // Ctrl/Cmd+S → parent save (Redux / class handler)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      if (typeof onSaveRef.current === 'function') {
        onSaveRef.current();
      }
    });

    editor.addAction({
      id: 'format-document',
      label: 'Format Document',
      keybindings: [monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF],
      run: (ed) => {
        const action = ed.getAction('editor.action.formatDocument');
        if (action) action.run();
      },
    });

    editor.addAction({
      id: 'goto-next-error',
      label: 'Go to Next Problem',
      keybindings: [monaco.KeyCode.F8],
      run: (ed) => {
        const action = ed.getAction('editor.action.marker.next');
        if (action) action.run();
      },
    });

    editor.addAction({
      id: 'goto-prev-error',
      label: 'Go to Previous Problem',
      keybindings: [monaco.KeyMod.Shift | monaco.KeyCode.F8],
      run: (ed) => {
        const action = ed.getAction('editor.action.marker.prev');
        if (action) action.run();
      },
    });

    updateMarkerCounts(monaco, editor.getModel());

    const disposable = monaco.editor.onDidChangeMarkers((uris) => {
      const current = editor.getModel();
      if (!current) return;
      if (uris.some((u) => u.toString() === current.uri.toString())) {
        updateMarkerCounts(monaco, current);
      }
    });

    editor.onDidDispose(() => disposable.dispose());
  };

  useEffect(() => {
    const monaco = monacoRef.current;
    const editor = editorRef.current;
    if (!monaco || !editor) return;
    updateMarkerCounts(monaco, editor.getModel());
  }, [selectedPath, editorValue]);

  const handleChange = (value) => {
    if (typeof onEditorChange === 'function') {
      onEditorChange(value == null ? '' : value);
    }
  };

  if (!selectedPath) {
    return (
      <div className="vibe-coding-workspace__editor-empty text-muted p-4">
        Select a file to edit
      </div>
    );
  }

  const isPng = isPngFile(selectedPath);

  return (
    <div className="vibe-code-editor d-flex flex-column h-100">
      {isPng ? (
        <div className="vibe-code-editor__image-preview">
          {isFileLoading ? (
            <span className="text-muted">Loading image…</span>
          ) : imagePreviewUrl ? (
            <img src={imagePreviewUrl} alt={selectedPath} />
          ) : (
            <span className="text-muted" role="alert">
              Image preview is unavailable.
            </span>
          )}
        </div>
      ) : (
        <div className="vibe-code-editor__monaco flex-grow-1">
          <Editor
            height="100%"
            language={getFileLanguage(selectedPath)}
            value={editorValue}
            path={selectedPath || 'untitled'}
            theme={theme || 'light'}
            loading={isFileLoading ? 'Loading file…' : undefined}
            onChange={handleChange}
            onMount={handleEditorDidMount}
            options={{
              readOnly: Boolean(readOnly) || Boolean(isFileLoading),
              fontSize: 14,
              fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
              fontLigatures: true,
              minimap: { enabled: true, scale: 1 },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              insertSpaces: true,
              wordWrap: 'on',
              lineNumbers: 'on',
              renderWhitespace: 'selection',
              renderLineHighlight: 'all',
              bracketPairColorization: { enabled: true },
              guides: { bracketPairs: true, indentation: true },
              padding: { top: 8, bottom: 8 },
              smoothScrolling: true,
              cursorBlinking: 'smooth',
              cursorSmoothCaretAnimation: 'on',
              stickyScroll: { enabled: true },
              quickSuggestions: { other: true, comments: false, strings: true },
              suggestOnTriggerCharacters: true,
              acceptSuggestionOnEnter: 'on',
              snippetSuggestions: 'inline',
              formatOnPaste: true,
              formatOnType: false,
              autoClosingBrackets: 'languageDefined',
              autoClosingQuotes: 'languageDefined',
              autoIndent: 'full',
              folding: true,
              foldingStrategy: 'auto',
              showFoldingControls: 'mouseover',
              matchBrackets: 'always',
              links: true,
              colorDecorators: true,
              dragAndDrop: true,
              emptySelectionClipboard: true,
              copyWithSyntaxHighlighting: true,
              multiCursorModifier: 'alt',
              accessibilitySupport: 'off',
              glyphMargin: true,
              lightbulb: { enabled: true },
            }}
          />
        </div>
      )}
      <div className="vibe-code-editor__statusbar">
        {isPng ? (
          <span>PNG image</span>
        ) : (
          <>
            <span className={markerInfo.errors ? 'text-danger' : 'text-muted'}>
              {markerInfo.errors} error{markerInfo.errors === 1 ? '' : 's'}
            </span>
            <span className={markerInfo.warnings ? 'text-warning' : 'text-muted'}>
              {markerInfo.warnings} warning{markerInfo.warnings === 1 ? '' : 's'}
            </span>
          </>
        )}
        <span className="text-muted ml-auto">
          {getFileLanguage(selectedPath)} · {selectedPath}
        </span>
      </div>
    </div>
  );
};

CodeEditor.propTypes = {
  editorValue: PropTypes.string,
  imagePreviewUrl: PropTypes.string,
  selectedPath: PropTypes.string,
  isFileLoading: PropTypes.bool,
  onEditorChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  theme: PropTypes.string,
  readOnly: PropTypes.bool,
};

CodeEditor.defaultProps = {
  editorValue: '',
  imagePreviewUrl: '',
  selectedPath: '',
  isFileLoading: false,
  onSave: null,
  theme: 'light',
  readOnly: false,
};

export default CodeEditor;
